import JSZip from 'jszip';

/**
 * 글꼴 들이기 — 파일·압축 폴더에서 **확인 표를 만드는 데까지**의 순수 로직 (2026-08-20).
 *
 * 화면(FontWorkspace)에서 떼어 둔 이유는 둘이다:
 *   · 세 경로(파일·압축·주소)가 **같은 확인 표**를 쓴다 — 표를 만드는 규칙이 한 곳에 있어야 한다.
 *   · 규칙(형식 판별·이름 짐작·중복 제거)은 화면 없이 시험할 수 있다.
 *
 * ⚠ 압축은 **브라우저에서 푼다.** 서버로는 확인된 항목만 기존 등록 API 로 하나씩 올라가므로
 *   서버에 압축 해제 경로를 새로 뚫지 않는다(진행률·부분 실패도 자연스럽게 화면에 남는다).
 */

/** chat-server 상한과 같은 값 — 올리기 전에 화면이 먼저 걸러 준다. */
export const FONT_MAX_BYTES = 8 * 1024 * 1024;

export type FontFormat = 'woff2' | 'woff' | 'ttf' | 'otf';

/** 서버와 같은 매직바이트 표. 확장자만 믿으면 PNG 를 .ttf 로 바꿔 올린 것을 못 거른다. */
const MAGIC: ReadonlyArray<{ format: FontFormat; magic: readonly string[] }> = [
  { format: 'woff2', magic: ['774f4632'] },
  { format: 'woff', magic: ['774f4646'] },
  { format: 'otf', magic: ['4f54544f'] },
  { format: 'ttf', magic: ['00010000', '74727565', '74746366'] },
];

export function detectFontFormat(head: Uint8Array): FontFormat | null {
  if (head.length < 4) return null;
  const hex = Array.from(head.subarray(0, 4)).map((b) => b.toString(16).padStart(2, '0')).join('');
  return MAGIC.find((m) => m.magic.includes(hex))?.format ?? null;
}

/** 파일 이름에 글꼴 확장자가 붙어 있나 — 압축 안에서 후보를 고를 때 1차로 쓴다. */
export function hasFontExtension(name: string): boolean {
  return /\.(woff2|woff|ttf|otf|ttc)$/i.test(name);
}

/** 굵기 이름 → 숫자. 파일 이름에 흔히 쓰이는 표기를 모아 둔다(긴 것을 먼저 본다). */
const WEIGHT_WORDS: ReadonlyArray<[RegExp, number]> = [
  [/extra[\s_-]?black|ultra[\s_-]?black/i, 900],
  [/extra[\s_-]?bold|ultra[\s_-]?bold|heavy/i, 800],
  [/semi[\s_-]?bold|demi[\s_-]?bold/i, 600],
  [/black/i, 900],
  [/bold/i, 700],
  [/medium/i, 500],
  [/extra[\s_-]?light|ultra[\s_-]?light/i, 200],
  [/thin|hairline/i, 100],
  [/light/i, 300],
  [/regular|normal|book/i, 400],
];

export interface GuessedFace {
  family: string;
  weight: number;
  style: 'normal' | 'italic';
}

/**
 * 파일 이름에서 가족·굵기·기울임을 **짐작한다** — 채워 주기만 하고, 화면에서 고칠 수 있다.
 * (`NanumGothic-Bold.woff2` → 나눔고딕이 아니라 `NanumGothic` / 700 / 보통. 한글 이름은
 *  파일 이름에 없으면 알 수 없다 — 문서가 부르는 이름은 사람이 아는 것이라 손으로 적는다.)
 */
export function guessFace(fileName: string): GuessedFace {
  const base = fileName.replace(/^.*[/\\]/, '').replace(/\.(woff2|woff|ttf|otf|ttc)$/i, '');
  const style: 'normal' | 'italic' = /italic|oblique/i.test(base) ? 'italic' : 'normal';
  let weight = 400;
  for (const [re, w] of WEIGHT_WORDS) {
    if (re.test(base)) { weight = w; break; }
  }
  // 숫자 굵기가 직접 적힌 경우(NotoSansKR-700) 가 더 확실하다.
  const num = /[-_](100|200|300|400|500|600|700|800|900)(?![0-9])/.exec(base);
  if (num) weight = Number(num[1]);

  /* 뒤에 붙은 굵기·기울임 표기를 **하나씩 되풀이해** 떼어 낸다.
   * ⚠ 한 번만 떼면 `Roboto-BoldItalic` 이 `RobotoItalic` 로 남는다 — 두 표기가 구분 기호 없이
   *   붙어 오는 것이 흔하다(실제로 여기서 한 번 틀렸다). 긴 표기(ExtraBold)가 먼저 걸리도록
   *   교체 목록 순서를 지킨다. */
  const TAIL = /[-_ ]?(extra[\s_-]?black|ultra[\s_-]?black|extra[\s_-]?bold|ultra[\s_-]?bold|semi[\s_-]?bold|demi[\s_-]?bold|extra[\s_-]?light|ultra[\s_-]?light|hairline|regular|oblique|medium|italic|normal|light|heavy|black|thin|book|bold|[1-9]00)$/i;
  let family = base;
  for (let guard = 0; guard < 6 && TAIL.test(family); guard += 1) {
    family = family.replace(TAIL, '');
  }
  family = family.replace(/[-_]+/g, ' ').trim();
  // 이름이 통째로 사라졌다면(글꼴 이름이 'Black' 인 경우 등) 원래 이름을 쓴다.
  if (family === '') family = base;
  return { family, weight, style };
}

/** 확인 표 한 줄의 상태. 화면 문구와 뱃지 색이 여기에 붙는다. */
export type IntakeStatus =
  | 'new'        // 새로 등록된다
  | 'replace'    // 같은 얼굴이 이미 있다 — 갈아끼운다
  | 'dupe'       // 이번 묶음 안에서 겹친다(작은 쪽만 남긴다)
  | 'skip'       // 글꼴 파일이 아니다
  | 'bad'        // 확장자는 글꼴인데 내용이 아니다
  | 'big'        // 8MB 를 넘는다
  | 'denied'     // 주소를 받아 줄 수 없다(사설망·평문 http)
  | 'unreachable'// 주소에서 받지 못했다(404·시간 초과)
  | 'failed'     // 등록하다 실패했다
  | 'done';      // 등록됐다

export interface IntakeRow {
  key: string;
  /** 압축 안 경로를 포함한 표시용 이름. */
  path: string;
  name: string;
  family: string;
  weight: number;
  style: 'normal' | 'italic';
  format: FontFormat | null;
  sizeBytes: number;
  status: IntakeStatus;
  /** 왜 못 쓰는지 / 왜 실패했는지 — "등록 실패" 만 뜨면 다음에 할 일을 알 수 없다. */
  reason?: string;
  /** 파일 경로일 때만. 주소 등록은 url 을 쓴다. */
  file?: File;
  url?: string;
}

/** 같은 얼굴이면 이 문자열이 같다 — 겹침 판정의 열쇠. */
export function faceKey(family: string, weight: number, style: string): string {
  return `${family.trim().toLowerCase()}|${weight}|${style}`;
}

/** 형식 우선순위 — 같은 얼굴이 여러 형식으로 들어오면 **작은 쪽**(woff2)만 남긴다. */
const FORMAT_RANK: Record<FontFormat, number> = { woff2: 0, woff: 1, otf: 2, ttf: 3 };

async function headBytes(blob: Blob): Promise<Uint8Array> {
  return new Uint8Array(await blob.slice(0, 4).arrayBuffer());
}

/** 파일 하나 → 확인 표 한 줄. 형식은 **내용**으로 가린다. */
export async function rowFromFile(file: File, index: number): Promise<IntakeRow> {
  const guess = guessFace(file.name);
  const base: IntakeRow = {
    key: `f${index}-${file.name}`,
    path: '',
    name: file.name,
    family: guess.family,
    weight: guess.weight,
    style: guess.style,
    format: null,
    sizeBytes: file.size,
    status: 'new',
    file,
  };
  if (!hasFontExtension(file.name)) {
    return { ...base, status: 'skip', reason: '글꼴 파일이 아닙니다' };
  }
  const fmt = detectFontFormat(await headBytes(file));
  if (!fmt) {
    return {
      ...base,
      status: 'bad',
      reason: `글꼴 파일이 아닙니다 — 확장자만 ${file.name.replace(/^.*\./, '.')} 인 다른 파일로 보입니다`,
    };
  }
  if (file.size > FONT_MAX_BYTES) {
    return {
      ...base, format: fmt, status: 'big',
      reason: `8MB 를 넘습니다 (${formatBytes(file.size)}) — woff2 로 변환하면 대개 1/3 로 줄어듭니다`,
    };
  }
  return { ...base, format: fmt };
}

/**
 * 압축 폴더 → 확인 표.
 *
 * 하위 폴더까지 훑되 **글꼴만** 고른다. 설명서·라이선스·견본 이미지는 건너뛴 줄로 남겨
 * "몇 개를 왜 넘어갔는지" 를 보여 준다 — 조용히 빼면 빠뜨린 것과 구분되지 않는다.
 */
export async function rowsFromZip(zipFile: File): Promise<IntakeRow[]> {
  const zip = await JSZip.loadAsync(zipFile);
  const rows: IntakeRow[] = [];
  let i = 0;

  const entries = Object.values(zip.files).filter((e) => !e.dir);
  for (const entry of entries) {
    i += 1;
    const full = entry.name;
    // macOS 압축이 끼워 넣는 자원 포크·숨김 파일은 아예 보이지 않게 한다(표를 어지럽히기만 한다).
    if (/(^|\/)(__MACOSX\/|\._|\.DS_Store)/.test(full)) continue;

    const name = full.replace(/^.*\//, '');
    const path = full.slice(0, full.length - name.length);
    if (!hasFontExtension(name)) {
      rows.push({
        key: `z${i}-${full}`, path, name,
        family: '', weight: 400, style: 'normal', format: null, sizeBytes: 0,
        status: 'skip', reason: '글꼴 파일이 아닙니다',
      });
      continue;
    }
    const blob = await entry.async('blob');
    const fmt = detectFontFormat(await headBytes(blob));
    const guess = guessFace(name);
    const file = new File([blob], name);
    const row: IntakeRow = {
      key: `z${i}-${full}`, path, name,
      family: guess.family, weight: guess.weight, style: guess.style,
      format: fmt, sizeBytes: blob.size, status: 'new', file,
    };
    if (!fmt) {
      rows.push({ ...row, status: 'bad', reason: '글꼴 파일이 아닙니다 — 확장자만 글꼴인 다른 파일로 보입니다' });
    } else if (blob.size > FONT_MAX_BYTES) {
      rows.push({
        ...row, status: 'big',
        reason: `8MB 를 넘습니다 (${formatBytes(blob.size)}) — woff2 로 변환하면 대개 1/3 로 줄어듭니다`,
      });
    } else {
      rows.push(row);
    }
  }
  return markDuplicates(rows);
}

/**
 * 이번 묶음 안에서 같은 얼굴이 여럿이면 **작은 형식만** 남기고 나머지는 `dupe` 로 표시한다.
 * (배포처 압축은 보통 `woff2/` 와 `otf/` 를 함께 담아 준다 — 둘 다 올리면 뒤엣것이 앞엣것을 덮는다.)
 */
export function markDuplicates(rows: IntakeRow[]): IntakeRow[] {
  const best = new Map<string, IntakeRow>();
  for (const r of rows) {
    if (r.status !== 'new' || !r.format) continue;
    const k = faceKey(r.family, r.weight, r.style);
    const cur = best.get(k);
    if (!cur || FORMAT_RANK[r.format] < FORMAT_RANK[cur.format!]) best.set(k, r);
  }
  return rows.map((r) => {
    if (r.status !== 'new' || !r.format) return r;
    const k = faceKey(r.family, r.weight, r.style);
    const winner = best.get(k);
    if (winner && winner.key !== r.key) {
      return { ...r, status: 'dupe' as IntakeStatus, reason: `같은 얼굴의 ${winner.format} 판을 대신 씁니다` };
    }
    return r;
  });
}

export function formatBytes(n: number): string {
  if (n >= 1024 * 1024) return `${(n / 1024 / 1024).toFixed(1)}MB`;
  if (n >= 1024) return `${Math.round(n / 1024)}KB`;
  return `${n}B`;
}

/** 파일 → base64(접두사 제거). 큰 파일도 메인 스레드를 오래 잡지 않게 FileReader 를 쓴다. */
export function fileToBase64(file: Blob): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onerror = () => reject(new Error('파일을 읽지 못했습니다'));
    reader.onload = () => {
      const result = String(reader.result ?? '');
      const comma = result.indexOf(',');
      resolve(comma >= 0 ? result.slice(comma + 1) : result);
    };
    reader.readAsDataURL(file);
  });
}
